import { useEffect, useState } from 'react'
import { publicApi } from '@/lib/api'

interface SubProcessor {
  name: string
  url: string | null
  role: string
  dataCategories: string[]
  location: string
  dpaUrl: string | null
  dpaVersion: string | null
  sccVersion: string | null
  lastReviewedAt: string | null
}

interface Response {
  generatedAt: string
  lastReviewedAt: string | null
  legalBasis: string
  items: SubProcessor[]
}

const LOCATION_LABEL: Record<string, string> = {
  EU: 'Unión Europea',
  EU_ADEQUACY: 'Tercer país con decisión de adecuación (art. 45 RGPD)',
  EU_SCC: 'Tercer país con Cláusulas Contractuales Tipo SCC (art. 46.2.c RGPD)',
  US: 'Estados Unidos',
  UK: 'Reino Unido',
  OTHER: 'Otro tercer país',
}

const ROLE_LABEL: Record<string, string> = {
  LLM: 'Modelo de lenguaje (LLM)',
  EMBEDDINGS: 'Embeddings / búsqueda semántica',
  EMAIL_PROVIDER: 'Proveedor de email',
  SMS_PROVIDER: 'Proveedor de SMS',
  WHATSAPP_PROVIDER: 'Proveedor de WhatsApp',
  VOICE_PROVIDER: 'Proveedor de voz',
  STORAGE: 'Almacenamiento de ficheros',
  BACKUP: 'Backups',
  REVIEWS: 'Plataforma de reseñas',
  HOSTING: 'Hosting',
  DNS: 'DNS',
  OTHER: 'Otro',
}

export function SubProcessorsPublicPage() {
  const [data, setData] = useState<Response | null>(null)
  const [err, setErr] = useState<string | null>(null)

  useEffect(() => {
    document.title = 'Sub-procesadores — JR2'
    document.documentElement.lang = 'es'
  }, [])

  useEffect(() => {
    publicApi<Response>('/p/legal/sub-procesadores')
      .then(setData)
      .catch((e) => {
        console.error(e)
        setErr('No hemos podido cargar la lista. Vuelve a intentarlo en unos minutos.')
      })
  }, [])

  if (err) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center p-6">
        <div role="alert" className="bp-card p-6 max-w-md w-full text-sm text-red-700">
          {err}
        </div>
      </div>
    )
  }
  if (!data) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center p-6">
        <div role="status" aria-live="polite" className="text-sm text-ink-700/70">
          Cargando…
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-surface bp-grid p-6">
      <div className="max-w-4xl mx-auto">
        <header className="mb-6">
          <div className="text-[10px] font-mono uppercase tracking-widest text-blueprint-700">
            JR2 MKT · legal
          </div>
          <h1 className="text-2xl font-medium tracking-tight">Sub-procesadores</h1>
          <p className="text-sm text-ink-700/70 mt-2 max-w-2xl">
            Esta página lista los terceros que tratan datos personales por nuestra cuenta
            (sub-encargados del tratamiento, RGPD art. 28). Mantenemos esta información
            actualizada en cumplimiento del deber de transparencia (RGPD art. 13).
          </p>
          {data.lastReviewedAt && (
            <p className="text-[11px] text-ink-700/60 mt-1">
              Datos verificados por última vez el{' '}
              {new Date(data.lastReviewedAt).toLocaleDateString('es-ES')}.
            </p>
          )}
        </header>

        <div className="bp-card overflow-hidden">
          <table className="w-full text-sm">
            <caption className="sr-only">
              Lista de sub-procesadores con los que JR2 comparte datos personales,
              incluyendo su rol, ubicación, categorías de datos y enlace al DPA.
            </caption>
            <thead className="bg-ink-900/5 text-ink-700/70 text-xs">
              <tr>
                <th scope="col" className="text-left px-3 py-2">Proveedor</th>
                <th scope="col" className="text-left px-3 py-2">Rol</th>
                <th scope="col" className="text-left px-3 py-2">Ubicación</th>
                <th scope="col" className="text-left px-3 py-2">Categorías de datos</th>
                <th scope="col" className="text-left px-3 py-2">DPA</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((sp, i) => (
                <tr key={`${sp.name}-${i}`} className="border-t border-ink-900/5 align-top">
                  <td className="px-3 py-2">
                    {sp.url ? (
                      <a
                        href={sp.url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-blueprint-700 underline"
                      >
                        {sp.name}
                      </a>
                    ) : (
                      sp.name
                    )}
                  </td>
                  <td className="px-3 py-2 text-ink-700/80">{ROLE_LABEL[sp.role] ?? sp.role}</td>
                  <td className="px-3 py-2 text-ink-700/80">
                    {LOCATION_LABEL[sp.location] ?? sp.location}
                    {sp.sccVersion && (
                      <div className="text-[11px] text-ink-700/60 mt-0.5">
                        SCC {sp.sccVersion}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-ink-700/80">
                    {sp.dataCategories.length > 0 ? (
                      <ul className="list-disc pl-4">
                        {sp.dataCategories.map((c, j) => (
                          <li key={j}>{c}</li>
                        ))}
                      </ul>
                    ) : (
                      <span className="text-ink-700/40">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {sp.dpaUrl ? (
                      <a
                        href={sp.dpaUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="text-blueprint-700 underline text-xs"
                      >
                        Ver DPA{sp.dpaVersion ? ` (${sp.dpaVersion})` : ''}
                      </a>
                    ) : (
                      <span className="text-ink-700/40 text-xs">—</span>
                    )}
                  </td>
                </tr>
              ))}
              {data.items.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-3 py-6 text-center text-ink-700/60">
                    No hay sub-procesadores publicados.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <footer className="mt-6 text-[11px] text-ink-700/60 max-w-2xl">
          Base legal: {data.legalBasis}. Para ejercer tus derechos (acceso, rectificación,
          supresión, oposición, portabilidad, limitación) sobre tus datos en relación con
          cualquiera de estos sub-procesadores, utiliza el formulario en{' '}
          <a href="/p/rgpd" className="text-blueprint-700 underline">
            /p/rgpd
          </a>
          .
        </footer>
      </div>
    </div>
  )
}
